// Comprobante de recepción de dinero del cobro en efectivo en ruta (pedido de Rodrigo, 2026-11-01:
// «se genera un comprobante… compartirlo al cliente»). Es una IMAGEN: por WhatsApp se ve directo en el
// chat, sin abrir nada. Los datos son los que guardó el ERP (número CR-…, hora, montos, quién recibió);
// aquí solo se dibujan. Lo usan la app del vendedor (compartir) y tesorería (volver a verlo).

export interface VistaRecepcion {
  recepcionId: string;
  numero: string;
  estado: 'PENDIENTE' | 'INGRESADA' | 'ANULADA';
  medio: 'EFECTIVO';
  total: string;
  observacion: string | null;
  recibidoEn: string;
  empresa: { razonSocial: string; rut: string };
  cliente: { entidadId: string; razonSocial: string; rut: string | null };
  vendedor: { id: string; codigo: string; nombre: string };
  recibidoPor: { id: string; nombre: string };
  documentos: Array<{
    documentoId: string;
    heredado: boolean;
    tipoDte: number | null;
    folio: string | null;
    saldoAntes: string;
    monto: string;
  }>;
  resolucion: {
    estado: 'INGRESADA' | 'ANULADA';
    detalle: string | null;
    resueltoEn: string;
    resueltoPor: { id: string; nombre: string };
    recibo: { id: string; numero: string } | null;
  } | null;
}

const NOMBRE: Record<number, string> = {
  33: 'Factura',
  34: 'Factura exenta',
  39: 'Boleta',
  41: 'Boleta exenta',
  56: 'Nota de débito',
};
export const nombreDocumento = (d: { tipoDte: number | null; folio: string | null }) =>
  `${NOMBRE[d.tipoDte ?? 33] ?? 'Documento'} N° ${d.folio ?? '—'}`;

const clp = (v: string | number) =>
  `$${Math.round(Number(v)).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;

/** «01-11-2026 15:32» en hora de Chile. */
export const fechaHoraChile = (iso: string) =>
  new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(new Date(iso))
    .replace(',', '');

/** Nombre del archivo al compartir o descargar: MADECI-CR-000012.png */
export const archivoComprobante = (v: Pick<VistaRecepcion, 'numero'>) => `MADECI-${v.numero}.png`;

const FUENTE = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
const ANCHO = 1080;
const MARGEN = 72;

/** Parte un texto en líneas que caben en `ancho`. */
function partir(ctx: CanvasRenderingContext2D, texto: string, ancho: number): string[] {
  const lineas: string[] = [];
  let actual = '';
  for (const palabra of texto.split(/\s+/)) {
    const prueba = actual ? `${actual} ${palabra}` : palabra;
    if (ctx.measureText(prueba).width > ancho && actual) {
      lineas.push(actual);
      actual = palabra;
    } else actual = prueba;
  }
  if (actual) lineas.push(actual);
  return lineas;
}

/** Dibuja el comprobante y lo entrega como PNG. */
export async function dibujarComprobante(v: VistaRecepcion): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const ctx0 = canvas.getContext('2d');
  if (!ctx0) throw new Error('Este navegador no puede dibujar el comprobante');
  // Dos pasadas: la primera mide el alto, la segunda dibuja.
  const pasada = (ctx: CanvasRenderingContext2D, dibujar: boolean): number => {
    let y = 0;
    const texto = (
      t: string,
      x: number,
      tamano: number,
      opciones: { peso?: number; color?: string; alinear?: CanvasTextAlign } = {},
    ) => {
      ctx.font = `${opciones.peso ?? 400} ${tamano}px ${FUENTE}`;
      ctx.fillStyle = opciones.color ?? '#1a1a1a';
      ctx.textAlign = opciones.alinear ?? 'left';
      if (dibujar) ctx.fillText(t, x, y);
    };
    const linea = (color = '#e5e7eb', grosor = 2) => {
      if (!dibujar) return;
      ctx.fillStyle = color;
      ctx.fillRect(MARGEN, y, ANCHO - MARGEN * 2, grosor);
    };
    // Banda superior
    if (dibujar) {
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, ANCHO, 190);
    }
    y = 96;
    texto('MADECI', MARGEN, 64, { peso: 800, color: '#ffffff' });
    ctx.font = `800 64px ${FUENTE}`;
    texto('.', MARGEN + ctx.measureText('MADECI').width, 64, { peso: 800, color: '#ff6b81' });
    y = 150;
    texto('Comprobante de recepción de dinero', MARGEN, 34, { color: 'rgba(255,255,255,.78)' });
    y = 280;
    texto(`N° ${v.numero}`, MARGEN, 58, { peso: 800 });
    y = 330;
    texto(fechaHoraChile(v.recibidoEn), MARGEN, 32, { color: '#6b7280' });
    y = 380;
    texto(`${v.empresa.razonSocial} · RUT ${v.empresa.rut}`, MARGEN, 28, { color: '#6b7280' });
    y = 420;
    linea();
    // Cliente
    y = 480;
    texto('RECIBIMOS DE', MARGEN, 26, { peso: 700, color: '#6b7280' });
    ctx.font = `700 40px ${FUENTE}`;
    for (const l of partir(ctx, v.cliente.razonSocial, ANCHO - MARGEN * 2)) {
      y += 54;
      texto(l, MARGEN, 40, { peso: 700 });
    }
    if (v.cliente.rut) {
      y += 44;
      texto(`RUT ${v.cliente.rut}`, MARGEN, 30, { color: '#6b7280' });
    }
    y += 40;
    linea();
    // Documentos
    y += 56;
    texto('DOCUMENTO', MARGEN, 26, { peso: 700, color: '#6b7280' });
    texto('ABONO', ANCHO - MARGEN, 26, { peso: 700, color: '#6b7280', alinear: 'right' });
    for (const d of v.documentos) {
      y += 58;
      texto(nombreDocumento(d), MARGEN, 34);
      texto(clp(d.monto), ANCHO - MARGEN, 34, { peso: 700, alinear: 'right' });
      const resto = Number(d.saldoAntes) - Number(d.monto);
      if (resto > 0) {
        y += 40;
        texto(`Queda por pagar ${clp(resto)}`, MARGEN, 26, { color: '#6b7280' });
      }
    }
    y += 40;
    linea('#1a1a1a', 3);
    y += 70;
    texto('Total recibido en efectivo', MARGEN, 34, { peso: 700 });
    texto(clp(v.total), ANCHO - MARGEN, 52, { peso: 800, alinear: 'right' });
    y += 40;
    linea();
    y += 58;
    texto('RECIBIDO POR', MARGEN, 26, { peso: 700, color: '#6b7280' });
    y += 48;
    texto(
      `${v.recibidoPor.nombre || v.vendedor.nombre}${v.vendedor.codigo ? ` (${v.vendedor.codigo})` : ''}`,
      MARGEN,
      34,
      { peso: 700 },
    );
    if (v.observacion) {
      ctx.font = `400 28px ${FUENTE}`;
      for (const l of partir(ctx, `Nota: ${v.observacion}`, ANCHO - MARGEN * 2)) {
        y += 42;
        texto(l, MARGEN, 28, { color: '#6b7280' });
      }
    }
    y += 60;
    if (dibujar) {
      ctx.fillStyle = '#f5f6f8';
      ctx.fillRect(MARGEN, y - 8, ANCHO - MARGEN * 2, 150);
    }
    ctx.font = `400 26px ${FUENTE}`;
    const pie = [
      'Su pago queda registrado en su cuenta corriente y se formaliza al ingresar a tesorería.',
      'Consultas: pagos@madeci.cl',
    ];
    y += 32;
    for (const parrafo of pie)
      for (const l of partir(ctx, parrafo, ANCHO - MARGEN * 2 - 48)) {
        texto(l, MARGEN + 24, 26, { color: '#4b5563' });
        y += 38;
      }
    y += 60;
    // Anulado: que no sirva como prueba de pago.
    if (dibujar && v.estado === 'ANULADA') {
      ctx.save();
      ctx.translate(ANCHO / 2, y / 2);
      ctx.rotate(-Math.PI / 7);
      ctx.font = `900 170px ${FUENTE}`;
      ctx.fillStyle = 'rgba(200, 16, 46, .28)';
      ctx.textAlign = 'center';
      ctx.fillText('ANULADO', 0, 0);
      ctx.restore();
    }
    return y;
  };
  const alto = pasada(ctx0, false);
  canvas.width = ANCHO;
  canvas.height = Math.ceil(alto);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  pasada(ctx, true);
  return new Promise((ok, mal) =>
    canvas.toBlob((b) => (b ? ok(b) : mal(new Error('No se pudo armar la imagen'))), 'image/png'),
  );
}
